<template>
  <div class="map-settings-view">

    <div class="setting-row">
      <span class="setting-label">Lock Map to north</span>
      <div class="form-check form-switch m-0">
        <input
          class="form-check-input setting-toggle"
          type="checkbox"
          role="switch"
          :checked="settings.lockToNorth"
          @change="onToggle('lockToNorth', $event)"
        />
      </div>
    </div>

    <div v-if="showNationalScaleLock" class="setting-row">
      <div class="flex-grow-1 pe-3">
        <span class="setting-label">Lock to 1:50k scale</span>
        <div class="setting-sub">Keeps National+ on the 1:50,000 series instead of switching to 1:25,000 as you zoom in.</div>
      </div>
      <div class="form-check form-switch m-0">
        <input
          class="form-check-input setting-toggle"
          type="checkbox"
          role="switch"
          :checked="settings.lockNationalTo50k"
          @change="onToggle('lockNationalTo50k', $event)"
        />
      </div>
    </div>

    <div class="setting-row setting-row--nav" @click="$emit('navigate', 'mapProjection')">
      <span class="setting-label">Map projection</span>
      <span class="material-icons setting-chevron">chevron_right</span>
    </div>

    <div class="setting-row setting-row--nav" @click="$emit('navigate', 'mapElevation')">
      <span class="setting-label">3D Elevation Exaggeration</span>
      <span class="material-icons setting-chevron">chevron_right</span>
    </div>

    <button class="reset-btn mt-4" @click="resetToDefault">Reset to default</button>

  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import emitter from '../../../utils/bus';
import { isNationalStyleSlug } from '../../../utils/models/MapStyles';
import {
  getMapSettings,
  saveMapSettings,
  applyMapSettings,
  DEFAULT_MAP_SETTINGS,
  MapSettings,
} from '../../../utils/mapSettings';

export default defineComponent({
  name: 'MapSettingsView',
  emits: ['navigate'],

  props: {
    mapInstanceName: { type: String, default: 'map' },
  },

  data() {
    return {
      settings: getMapSettings(),
    };
  },

  computed: {
    /**
     * The lock only has anything to cap on National+ — every other style is
     * drawn without the `national-overlay` source. Hidden rather than disabled
     * so Map settings doesn't carry a permanently dead row, and shown when the
     * current style is unknown so it can't vanish if the store isn't wired.
     */
    showNationalScaleLock(): boolean {
      const style = (this as any).$store?.state?.mapStyles?.currentMapStyle;
      if (!style) return true;
      return isNationalStyleSlug(style.toString?.());
    },
  },

  methods: {
    getMap(): any {
      return (window as Record<string, any>)[this.mapInstanceName] || null;
    },

    onToggle(key: 'lockToNorth' | 'lockNationalTo50k', e: Event) {
      this.settings[key] = (e.target as HTMLInputElement).checked;
      this.persistAndApply();
    },

    resetToDefault() {
      this.settings.lockToNorth = DEFAULT_MAP_SETTINGS.lockToNorth;
      this.settings.lockNationalTo50k = DEFAULT_MAP_SETTINGS.lockNationalTo50k;
      this.persistAndApply();
    },

    persistAndApply() {
      saveMapSettings(this.settings);
      const map = this.getMap();
      if (map) applyMapSettings(map, this.settings);
      // The dialog badges the National+ card while the scale lock is on, and it
      // reads the settings imperatively, so tell it they moved.
      emitter.emit('map-settings-changed', this.settings);
    },
  },
});
</script>

<style scoped>
.map-settings-view {
  padding: 0 0 24px;
}

.setting-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #2e2e2e;
}

.setting-row--nav {
  cursor: pointer;
  transition: background 0.15s;
}

.setting-row--nav:hover {
  background: rgba(255, 255, 255, 0.04);
}

.setting-label {
  font-size: 0.95rem;
}

.setting-sub {
  margin-top: 2px;
  font-size: 0.78rem;
  line-height: 1.3;
  color: #999;
}

.setting-chevron {
  color: #555;
}

.setting-toggle {
  width: 3em !important;
  height: 1.5em !important;
  background-color: #444 !important;
  border-color: #555 !important;
  cursor: pointer;
}

.setting-toggle:checked {
  background-color: #fddf00 !important;
  border-color: #fddf00 !important;
}

.reset-btn {
  display: block;
  width: calc(100% - 40px);
  margin: 0 20px;
  padding: 12px;
  background: #2d2d2d;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}

.reset-btn:hover {
  background: #3a3a3a;
}
</style>
